import React, { useCallback, useMemo, useRef, useState, useEffect, lazy, Suspense } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Menu, Sparkles, Eye, LogOut, AlertTriangle, PanelLeft, PanelLeftClose, CalendarDays } from 'lucide-react';
import { Sidebar } from './Sidebar';
// Lazy — the assistant drawer (plus marked + aiTools, ~28 kB) only loads the
// first time it's opened, not on every CRM page paint.
const AIAssistant = lazy(() => import('./AIAssistant').then(m => ({ default: m.AIAssistant })));
import { NotificationDropdown, type AttentionItem } from '../ui/NotificationDropdown';
import { useIntercambioThreads } from '../../lib/intercambio-message-queries';
import { useOmnichannelUnread } from '../../lib/omnichannel-queries';
import { useOpenTicketCount, useCompliancePendingCount, useSugerenciasNewCount, useBlogPendingCount } from '../../lib/ticket-queries';
import { useTheme } from '../../contexts/ThemeContext';
import { useCRM } from '../../contexts/CRMContext';
import { readPropertiesFiltersFromSession } from '../../lib/properties-filter-storage';
import { useAuth } from '../../contexts/AuthContext';
import { useAIQuota } from '../../hooks/useAIQuota';
import { trackPageView } from '../../lib/usageTracker';
import { supabase } from '../../lib/supabase';
import type { AIToolDeps } from '../../services/aiTools';
import { TourProvider } from '../tours/TourContext';
import { TourGuide } from '../tours/TourGuide';
import { MedalsBar } from '../ui/MedalsBar';

// Map route paths to Spanish titles
const routeTitles: Record<string, string> = {
  '/': 'Panel de Control',
  '/propiedades': 'Mis Propiedades',
  '/propiedades/nueva': 'Agregar Propiedad',
  '/prospectos': 'Prospectos',
  '/propietarios': 'Propietarios',
  '/proyectos': 'Proyectos',
  '/proyectos/nuevo': 'Crear Proyecto',
  '/calendario': 'Calendario',
  '/mensajes': 'Mensajería',
  '/reportes': 'Reportes',
  '/financiamiento': 'Financiamiento',
  '/colegas': 'Red de Colegas',
  '/coincidencias': 'Coincidencias de Red',
  '/requerimientos': 'Requerimientos',
  '/requerimientos/nuevo': 'Nuevo Requerimiento',
  '/intercambio/propiedades': 'Propiedades Intercambio',
  '/intercambio/requerimientos': 'Requerimientos Intercambio',
  '/equipo': 'Gestión de Equipo',
  '/configuracion': 'Configuración de Aura',
  '/pagina-web': 'Página Web',
  '/analisis': 'Análisis e Informes',
  '/membresia': 'Mi Membresía',
  '/reglamento': 'Reglamento',
  '/soporte': 'Soporte',
  '/sugerencias': 'Sugerencias de la Comunidad',
  '/blog-inmobiliario': 'Blog Inmobiliario',
  '/administrador': 'Panel de Plataforma',
  '/administrador/agencias': 'Agencias',
  '/administrador/usuarios': 'Usuarios',
  '/administrador/facturacion': 'Facturación',
  '/administrador/ventas': 'Prospectos SaaS',
  '/administrador/cumplimiento': 'Cumplimiento',
  '/administrador/contenido': 'Contenido y Portales',
  '/administrador/sistema': 'Sistema',
  '/administrador/tickets': 'Tickets de Soporte',
  '/novedades': 'Novedades',
};

function getTitle(pathname: string): string {
  if (routeTitles[pathname]) return routeTitles[pathname];
  if (pathname.startsWith('/propiedades/') && pathname.endsWith('/coincidencias')) return 'Coincidencias de Red';
  if (pathname.startsWith('/propiedades/') && pathname.endsWith('/editar')) return 'Editar Propiedad';
  if (pathname.startsWith('/propiedades/') && pathname.endsWith('/reporte')) return 'Reporte del Cliente';
  if (pathname.startsWith('/propiedades/') && pathname.endsWith('/imprimir')) return 'Ficha Imprimible';
  if (pathname.startsWith('/propiedades/')) return 'Detalle de Propiedad';
  if (pathname.startsWith('/proyectos/') && pathname.endsWith('/editar')) return 'Editar Proyecto';
  if (pathname.startsWith('/proyectos/')) return 'Detalle del Proyecto';
  if (pathname.startsWith('/prospectos/')) return 'Prospectos';
  if (pathname.startsWith('/propietarios/')) return 'Propietarios';
  if (pathname.startsWith('/requerimientos/') && pathname.endsWith('/coincidencias')) return 'Propiedades que Coinciden';
  if (pathname.startsWith('/requerimientos/') && pathname.endsWith('/editar')) return 'Editar Requerimiento';
  if (pathname.startsWith('/novedades')) return 'Novedades';
  if (pathname.startsWith('/administrador/agencias/')) return 'Detalle de Agencia';
  if (pathname.startsWith('/administrador/agentes/')) return 'Detalle de Agente';
  return 'Panel de Control';
}

// Map route paths to the sidebar menu item IDs
function routeToMenuId(pathname: string): string {
  if (pathname === '/') return 'dashboard';
  if (pathname.startsWith('/novedades')) return 'novedades';
  if (pathname.startsWith('/propiedades')) return 'properties';
  if (pathname.startsWith('/prospectos')) return 'leads';
  if (pathname.startsWith('/propietarios')) return 'tasks';
  if (pathname.startsWith('/proyectos')) return 'projects';
  if (pathname.startsWith('/calendario')) return 'calendario';
  if (pathname.startsWith('/mensajes')) return 'messages';
  if (pathname.startsWith('/reportes')) return 'reportes';
  if (pathname.startsWith('/financiamiento')) return 'financiamiento';
  if (pathname.startsWith('/colegas')) return 'colegas';
  if (pathname.startsWith('/coincidencias')) return 'coincidencias';
  if (pathname.startsWith('/requerimientos')) return 'requerimientos';
  if (pathname === '/intercambio/requerimientos') return 'exchange_requirements';
  if (pathname.startsWith('/intercambio')) return 'exchange_properties';
  if (pathname.startsWith('/membresia')) return 'membresia';
  if (pathname.startsWith('/reglamento')) return 'reglamento';
  if (pathname.startsWith('/soporte')) return 'soporte';
  if (pathname.startsWith('/sugerencias')) return 'sugerencias';
  if (pathname.startsWith('/blog-inmobiliario')) return 'blog_inmobiliario';
  if (pathname.startsWith('/equipo')) return 'equipo';
  if (pathname.startsWith('/configuracion')) return 'settings';
  if (pathname.startsWith('/pagina-web')) return 'website';
  if (pathname.startsWith('/analisis')) return 'analytics';
  // Admin sections — order specific → general.
  if (pathname.startsWith('/administrador/agencias')) return 'admin_agencias';
  if (pathname.startsWith('/administrador/agentes')) return 'admin_usuarios';
  if (pathname.startsWith('/administrador/usuarios')) return 'admin_usuarios';
  if (pathname.startsWith('/administrador/facturacion')) return 'admin_facturacion';
  if (pathname.startsWith('/administrador/ventas')) return 'admin_ventas';
  if (pathname.startsWith('/administrador/tickets')) return 'admin_tickets';
  if (pathname.startsWith('/administrador/cumplimiento')) return 'admin_cumplimiento';
  if (pathname.startsWith('/administrador/contenido')) return 'admin_contenido';
  if (pathname.startsWith('/administrador/sistema')) return 'admin_sistema';
  if (pathname.startsWith('/administrador')) return 'admin_resumen';
  return 'dashboard';
}

// Map sidebar menu IDs to routes
const menuIdToRoute: Record<string, string> = {
  dashboard: '/',
  novedades: '/novedades',
  properties: '/propiedades',
  leads: '/prospectos',
  tasks: '/propietarios',
  projects: '/proyectos',
  calendario: '/calendario',
  messages: '/mensajes',
  reportes: '/reportes',
  financiamiento: '/financiamiento',
  colegas: '/colegas',
  coincidencias: '/coincidencias',
  requerimientos: '/requerimientos',
  exchange_properties: '/intercambio/propiedades',
  exchange_requirements: '/intercambio/requerimientos',
  membresia: '/membresia',
  reglamento: '/reglamento',
  soporte: '/soporte',
  sugerencias: '/sugerencias',
  blog_inmobiliario: '/blog-inmobiliario',
  equipo: '/equipo',
  settings: '/configuracion',
  website: '/pagina-web',
  analytics: '/analisis',
  superadmin: '/administrador',
  admin_resumen: '/administrador',
  admin_agencias: '/administrador/agencias',
  admin_usuarios: '/administrador/usuarios',
  admin_facturacion: '/administrador/facturacion',
  admin_ventas: '/administrador/ventas',
  admin_cumplimiento: '/administrador/cumplimiento',
  admin_tickets: '/administrador/tickets',
  admin_contenido: '/administrador/contenido',
  admin_sistema: '/administrador/sistema',
};

export const AppLayout: React.FC = () => {
  const { theme, skin, healthState, assistantName, setTheme, setSkin } = useTheme();
  const { fetchProperties, fetchRequirements, fetchContacts, refreshContactCounts } = useCRM();
  const { isImpersonating, isSuspended, isReadOnly, isSuperAdmin, profile, agency, realProfile, stopImpersonation, updateUIPreference } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Sidebar badges, computed once here because the super admin's bell totals
  // the same numbers — a second copy of these hooks would double the queries
  // and realtime channels (useIntercambioThreads pulls every thread).
  const { totalUnread: intercambioUnread } = useIntercambioThreads();
  const { totalUnread: inboxUnread } = useOmnichannelUnread();
  const { openCount: openTickets } = useOpenTicketCount(isSuperAdmin);
  const { pendingCount: pendingCompliance } = useCompliancePendingCount(isSuperAdmin);
  const { newCount: newSugerencias } = useSugerenciasNewCount(isSuperAdmin);
  // Sales-scoped super admins have no Contenido y Portales (see Sidebar).
  const { pendingCount: pendingBlog } = useBlogPendingCount(isSuperAdmin && realProfile?.super_admin_scope !== 'sales');
  const badges = useMemo(() => ({
    // One inbox since 2026-08: Mensajes carries the meta channels AND the
    // intercambio colleague threads, so its badge is the sum of both.
    messages: inboxUnread + intercambioUnread,
    admin_tickets: openTickets,
    admin_cumplimiento: pendingCompliance,
    sugerencias: newSugerencias,
    admin_contenido: pendingBlog,
  }), [inboxUnread, intercambioUnread, openTickets, pendingCompliance, newSugerencias, pendingBlog]);
  // A super admin's bell counts what needs action — the sum of the badges
  // above, broken down per queue in the dropdown — not unread notifications.
  const attention = useMemo<AttentionItem[] | undefined>(() => isSuperAdmin ? [
    { label: 'Blog por revisar', count: badges.admin_contenido, path: menuIdToRoute.admin_contenido },
    { label: 'Tickets abiertos', count: badges.admin_tickets, path: menuIdToRoute.admin_tickets },
    { label: 'Cumplimiento pendiente', count: badges.admin_cumplimiento, path: menuIdToRoute.admin_cumplimiento },
    { label: 'Mensajes sin leer', count: badges.messages, path: menuIdToRoute.messages },
    { label: 'Sugerencias nuevas', count: badges.sugerencias, path: menuIdToRoute.sugerencias },
  ] : undefined, [isSuperAdmin, badges]);

  const handleStopImpersonation = () => {
    stopImpersonation();
    navigate('/administrador');
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Collapsed to the icon rail by default on desktop, overridable per user
  // (Ajustes de usuario → Navegación). Guarded on viewport so mobile (<lg,
  // where the collapse toggle is hidden) keeps the full-width drawer — a
  // collapsed w-20 drawer with clipped labels would be unusable.
  const prefersExpandedMenu = realProfile?.ui_preferences?.default_sidebar === 'expanded';
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => window.innerWidth >= 1024);
  // Re-apply when the preference itself changes. AppLayout never remounts, so
  // without this the setting would appear to do nothing until a full reload.
  // Keyed on the pref value only, so a manual collapse in between survives.
  useEffect(() => {
    if (window.innerWidth < 1024) return;
    setIsSidebarCollapsed(!prefersExpandedMenu);
  }, [prefersExpandedMenu]);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  // Latch: mount the lazy assistant on first open and keep it mounted so its
  // open/close animation still works — it just isn't in the eager bundle.
  const [assistantLoaded, setAssistantLoaded] = useState(false);
  useEffect(() => { if (isAssistantOpen) setAssistantLoaded(true); }, [isAssistantOpen]);
  // Pages open the assistant with window.dispatchEvent(new Event('aura:open'))
  // (e.g. right after starting the free trial in Configuración → IA).
  useEffect(() => {
    const open = () => setIsAssistantOpen(true);
    window.addEventListener('aura:open', open);
    return () => window.removeEventListener('aura:open', open);
  }, []);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const aiQuota = useAIQuota();

  const activeMenuId = routeToMenuId(location.pathname);
  const title = getTitle(location.pathname);

  // Usage telemetry: one event per route change (AppLayout only wraps
  // authenticated views, so anonymous portal traffic never logs).
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);

  const handleViewChange = (menuId: string) => {
    const route = menuIdToRoute[menuId];
    if (route) navigate(route);
  };

  // Hover/focus prefetch: fire the underlying list fetch when the user
  // points at a sidebar item so the destination page can render instantly
  // on click. Per-id throttle prevents a hover-around from spamming the
  // network — once a route is prefetched, we wait 5s before refiring.
  // Throttle is conservative because the request-id guard already drops
  // any duplicates that race with the destination page's mount fetch.
  const lastPrefetchAt = useRef<Record<string, number>>({});
  const handlePrefetchView = useCallback((view: string) => {
    const now = Date.now();
    if (now - (lastPrefetchAt.current[view] ?? 0) < 5000) return;
    lastPrefetchAt.current[view] = now;
    // Optional-call each fetcher: prefetch is a nice-to-have and must never
    // throw from a sidebar hover. A stale prod bundle that destructures a
    // fetcher the deployed CRMContext doesn't expose yet would otherwise
    // surface as "X is not a function" on every onMouseEnter.
    if (view === 'properties') {
      fetchProperties?.(1, false, readPropertiesFiltersFromSession());
    } else if (view === 'requerimientos') {
      fetchRequirements?.(1, false);
    } else if (view === 'leads' || view === 'tasks') {
      fetchContacts?.(1, false);
    }
  }, [fetchProperties, fetchRequirements, fetchContacts]);

  // Dependencies for the AI assistant's client-side tool executor. The
  // assistant runs tools with the user's own session (RLS = authz) and calls
  // the refreshers after writes so the kanban/lists reflect new records.
  const assistantToolDeps = useMemo<AIToolDeps>(() => ({
    supabase,
    profile: profile ?? null,
    isReadOnly,
    navigate,
    refreshContacts: () => { fetchContacts?.(1, false); },
    refreshContactCounts: () => { refreshContactCounts?.(); },
    // Settings mutations for the update_setting tool.
    setTheme,
    setSkin,
    updateUIPreference,
  }), [profile, isReadOnly, navigate, fetchContacts, refreshContactCounts, setTheme, setSkin, updateUIPreference]);

  return (
    <TourProvider>
    <div className={clsx(
      "flex min-h-screen relative",
      `skin-${skin}`,
      `health-${healthState}`,
      theme !== 'dark' ? "light-theme" : "dark-theme",
      theme === 'high-contrast' ? "high-contrast bg-[#0f172a] text-[#f8fafc]" : theme === 'light' ? "bg-[#f8fafc] text-[#0f172a]" : "bg-[#020617] text-[#f8fafc]"
    )}>
      {/* Health state ambient overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-0 transition-colors duration-700"
        style={{ backgroundColor: 'var(--health-tint)' }}
      />

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-50 lg:hidden animate-in fade-in duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        activeView={activeMenuId}
        onViewChange={(view) => {
          handleViewChange(view);
        }}
        badges={badges}
        onPrefetchView={handlePrefetchView}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        theme={theme}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      {/* Sidebar collapse toggle — lives outside the aside so the aside can
          keep overflow-hidden (smooth width transition). The toggle's `left`
          slides on the same easing/duration to track the sidebar's edge. */}
      <button
        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        className={clsx(
          'hidden lg:flex fixed top-10 w-8 h-8 rounded-full items-center justify-center border z-[70] shadow-sm',
          'transition-all duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
          isSidebarCollapsed ? 'left-[4rem]' : 'left-[17rem]',
          theme !== 'dark'
            ? 'bg-white border-slate-200 text-slate-400 hover:text-indigo-500'
            : 'bg-slate-900 border-white/10 text-slate-500 hover:text-indigo-400'
        )}
        aria-label={isSidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
      >
        {isSidebarCollapsed ? <PanelLeft size={14} /> : <PanelLeftClose size={14} />}
      </button>

      {/* overflow-clip, NOT overflow-hidden: a hidden box is still a scroll
          container, so every scrollIntoView({ block: 'center' }) in the app
          (last-visited card restore, tour steps, form-error focus) scrolled
          this column by half the header height and nothing could scroll it
          back — the "top bar pushed off the screen, half visible until I
          refresh" bug. clip cannot be scrolled by anything. min-w-0 keeps the
          flex min-size collapse that overflow-hidden used to give for free. */}
      <div className="flex-1 flex flex-col relative max-h-screen min-w-0 overflow-clip">
        {/* Background Gradients */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/10 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/5 blur-[100px] rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none" />

        {/* Suspension banner — visible when agency is suspended for non-payment */}
        {isSuspended && !isImpersonating && (
          <div className="z-20 px-4 sm:px-6 lg:px-10 py-2.5 bg-red-600 text-white border-b border-red-700 flex flex-wrap items-center justify-between gap-3 shadow-md">
            <div className="flex items-center gap-3 min-w-0">
              <AlertTriangle size={18} className="shrink-0" />
              <span className="text-sm font-medium">
                Tu cuenta ha sido suspendida por falta de pago. Renueva tu membresía para continuar.
              </span>
            </div>
          </div>
        )}

        {/* Impersonation banner — visible when a super_admin is "soft-logged-in" as another user */}
        {isImpersonating && (
          <div className="z-20 px-4 sm:px-6 lg:px-10 py-2.5 bg-amber-500 text-amber-950 border-b border-amber-600 flex flex-wrap items-center justify-between gap-3 shadow-md">
            <div className="flex items-center gap-3 min-w-0">
              <Eye size={18} className="shrink-0" />
              <div className="text-sm min-w-0">
                <span className="font-bold">Modo solo lectura</span>
                <span className="opacity-80"> · Viendo como </span>
                <span className="font-semibold truncate">
                  {profile?.full_name || profile?.email || 'usuario'}
                </span>
                {agency?.name && (
                  <span className="opacity-80"> · {agency.name}</span>
                )}
                {realProfile?.full_name && (
                  <span className="opacity-70 hidden sm:inline"> · Sesión real: {realProfile.full_name}</span>
                )}
              </div>
            </div>
            <button
              onClick={handleStopImpersonation}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-950 text-amber-50 text-xs font-bold hover:bg-amber-900 transition-colors shrink-0"
            >
              <LogOut size={14} /> Salir de la sesión
            </button>
          </div>
        )}

        <header className={clsx(
          "px-4 sm:px-6 lg:px-10 py-4 sm:py-6 flex justify-between items-center z-10 border-b backdrop-blur-xl",
          theme !== 'dark'
            ? "bg-white/70 border-slate-200/50"
            : "bg-[#020617]/50 border-white/5"
        )}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className={clsx(
                "p-3 rounded-xl transition-colors lg:hidden",
                theme !== 'dark' ? "hover:bg-slate-100 text-slate-600" : "hover:bg-white/5 text-slate-400"
              )}
            >
              <Menu size={24} />
            </button>
            <div>
              <h1 className={clsx(
                "text-lg sm:text-2xl font-black tracking-tight",
                theme === 'high-contrast' ? "text-slate-900" : theme === 'light' ? "text-slate-900" : "text-white"
              )}>
                {title}
              </h1>
            </div>
          </div>

          {/* Page-scoped action slot. Pages portal page-specific controls
              (e.g. Propiedades: search + view toggle + Nueva) into this via
              createPortal(#app-header-slot) to reclaim vertical space from
              their own toolbars. Empty on pages that don't use it — the
              flex-1 just holds the gap, same as before. Desktop only; mobile
              keeps its compact bar + FAB. */}
          <div id="app-header-slot" className="hidden md:flex flex-1 items-center min-w-0 px-2 lg:px-4" />

          <div className="flex items-center gap-4">
            {aiAnalysis && (
              <div className="animate-in fade-in slide-in-from-top-4 bg-indigo-500/10 border border-indigo-500/20 px-4 py-2.5 rounded-xl text-xs text-indigo-200 shadow-lg shadow-indigo-500/5 flex items-center gap-2">
                <Sparkles size={14} className="text-indigo-400" />
                <span>{aiAnalysis}</span>
              </div>
            )}

            {/* AI usage meter moved out of the top bar — it now lives inside
                the Aura assistant window (see AIAssistant header). */}

            {/* Medallas — dashboard only: earned in color, pending grayed
                with a how-to tooltip; CBR appears once the agency holds it. */}
            {location.pathname === '/' && <MedalsBar theme={theme === 'light' ? 'light' : 'dark'} />}

            {/* Calendario — lives in the top bar (alongside Notifications)
                instead of the sidebar so it stays one click away from any
                page without bloating the left nav. */}
            <button
              onClick={() => navigate('/calendario')}
              className={clsx(
                "relative p-2.5 rounded-xl border transition-all",
                location.pathname.startsWith('/calendario')
                  ? "text-white border-transparent"
                  : theme !== 'dark'
                    ? "bg-slate-100 border-slate-200 text-slate-600 hover:text-white"
                    : "bg-white/5 border-white/5 text-slate-400 hover:text-white"
              )}
              style={location.pathname.startsWith('/calendario') ? {
                backgroundColor: 'var(--accent-600)',
                borderColor: 'var(--accent-400)',
                boxShadow: '0 0 20px -4px var(--accent-glow)'
              } : undefined}
              onMouseEnter={e => { if (!location.pathname.startsWith('/calendario')) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--accent-600)'; }}
              onMouseLeave={e => { if (!location.pathname.startsWith('/calendario')) (e.currentTarget as HTMLButtonElement).style.backgroundColor = ''; }}
              title="Calendario"
              aria-label="Calendario"
            >
              <CalendarDays size={20} />
            </button>

            <NotificationDropdown theme={theme} attention={attention} />

            {/* Assistant hidden while impersonating: the edge fn keys tools off
                the REAL super_admin JWT, so serving it during an impersonated
                (read-only) session would mismatch the client executor. */}
            {!isImpersonating && (
            <button
              onClick={() => setIsAssistantOpen(prev => !prev)}
              className={clsx(
                "relative p-2.5 rounded-xl border transition-all group",
                isAssistantOpen
                  ? "text-white border-transparent"
                  : theme !== 'dark'
                    ? "bg-slate-100 border-slate-200 text-slate-600 hover:text-white"
                    : "bg-white/5 border-white/5 text-slate-400 hover:text-white"
              )}
              style={isAssistantOpen ? {
                backgroundColor: 'var(--accent-600)',
                borderColor: 'var(--accent-400)',
                boxShadow: '0 0 20px -4px var(--accent-glow)'
              } : undefined}
              onMouseEnter={e => { if (!isAssistantOpen) (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--accent-600)'; }}
              onMouseLeave={e => { if (!isAssistantOpen) (e.currentTarget as HTMLButtonElement).style.backgroundColor = ''; }}
              title={assistantName}
              aria-label={assistantName}
            >
              <Sparkles size={20} className={clsx('transition-all duration-300', isAssistantOpen ? 'rotate-12' : 'group-hover:rotate-12')} />
              <span className={clsx(
                "absolute top-1.5 right-1.5 w-2 h-2 bg-amber-400 rounded-full border",
                isAssistantOpen ? 'opacity-0' : 'opacity-100 animate-pulse',
                theme !== 'dark' ? 'border-white' : 'border-[#020617]'
              )} />
            </button>
            )}
          </div>
        </header>

        {/* Full-bleed routes (the website builder) drop the page padding so the
            tool fills the content area edge-to-edge — no wasted frame, bigger
            preview. The inbox keeps a thin gutter so the chat gets the height.
            Everything else keeps the standard page padding. */}
        <main className={clsx(
          "flex-1 overflow-y-auto z-10 custom-scrollbar",
          location.pathname.startsWith('/pagina-web') ? "p-0"
            : location.pathname.startsWith('/mensajes') ? "p-2 sm:p-4"
            : "p-4 sm:p-6 lg:p-10"
        )}>
          <Outlet />
        </main>
      </div>

      {assistantLoaded && !isImpersonating && (
        <Suspense fallback={null}>
          <AIAssistant
            theme={theme}
            isOpen={isAssistantOpen}
            onClose={() => setIsAssistantOpen(false)}
            assistantName={assistantName}
            userName={realProfile?.full_name}
            currentView={activeMenuId}
            hasSubscription={aiQuota.hasSubscription || realProfile?.role === 'super_admin'}
            usagePercentage={aiQuota.hasSubscription && !aiQuota.loading ? aiQuota.percentage : null}
            periodEnd={aiQuota.periodEnd}
            quotaError={aiQuota.error}
            onQuotaRefresh={aiQuota.refresh}
            onUsage={aiQuota.applyUsage}
            isTrial={aiQuota.isTrial}
            trialEligible={aiQuota.trialEligible}
            trialEnded={aiQuota.trialEnded}
            paidThrough={aiQuota.paidThrough}
            onStartTrial={aiQuota.startTrial}
            showShortcuts={(realProfile?.ui_preferences?.show_ai_shortcuts ?? false) as boolean}
            onToggleShortcuts={() => updateUIPreference('show_ai_shortcuts', !(realProfile?.ui_preferences?.show_ai_shortcuts ?? false))}
            toolDeps={assistantToolDeps}
          />
        </Suspense>
      )}

      {/* Guided-tour overlay (Herramientas clave) — needs router context, so it
          lives here rather than in the dashboard card that starts the tours. */}
      <TourGuide theme={theme === 'light' ? 'light' : 'dark'} />
    </div>
    </TourProvider>
  );
};
